import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EnvelopeSimple, ArrowLeft, CheckCircle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success(data.message || 'Check your email for reset instructions');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not start the reset. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1521114978264-2223e08aa0d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYmx1ZSUyMGFic3RyYWN0JTIwYXJjaGl0ZWN0dXJhbCUyMGxpbmVzfGVufDB8fHx8MTc4MDA3NzIwNHww&ixlib=rb-4.1.0&q=85)' }}
      >
        <div className="absolute inset-0 bg-[#0A1128]/80 flex items-center justify-center p-12">
          <div className="text-white max-w-lg">
            <h1 className="cabinet-grotesk text-4xl font-medium mb-2 text-[#C5A059]">PLANIVANCE AI</h1>
            <p className="text-xs uppercase tracking-[0.16em] text-[#C5A059]/70 mb-6">Client-Facing Financial Planning Platform</p>
            <p className="text-lg text-[#E2E8F0] leading-relaxed">
              Forgot your password? It happens. We'll send you a secure reset link.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#FDFCFB]">
        <div className="w-full max-w-md">
          <Link
            to="/auth"
            data-testid="forgot-back-link"
            className="inline-flex items-center gap-2 text-sm text-[#4A5568] hover:text-[#0A1128] mb-8 transition-colors"
          >
            <ArrowLeft size={16} /> Back to sign in
          </Link>

          {!sent ? (
            <>
              <div className="mb-8">
                <h2 className="cabinet-grotesk text-3xl font-medium text-[#0A1128] mb-2">Reset your password</h2>
                <p className="text-[#4A5568]">Enter the email you used to sign up and we'll send a reset link valid for 60 minutes.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-[#1A202C] mb-2 block">Email</Label>
                  <div className="relative">
                    <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={20} />
                    <Input
                      id="email"
                      type="email"
                      data-testid="forgot-email-input"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 border-slate-300 bg-white text-slate-900 rounded-md focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059]"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="forgot-submit-button"
                  disabled={loading || !email}
                  className="w-full bg-[#0A1128] text-white hover:bg-[#152145] rounded-md py-2.5 text-sm font-medium transition-colors"
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </Button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-success-state" className="text-center">
              <CheckCircle size={56} weight="duotone" className="text-[#2D6A4F] mx-auto mb-4" />
              <h2 className="cabinet-grotesk text-2xl font-medium text-[#0A1128] mb-2">Check your inbox</h2>
              <p className="text-[#4A5568] leading-relaxed mb-6">
                If an account exists for <strong className="text-[#1A202C]">{email}</strong>, we've sent you a password reset link. It expires in 60 minutes.
              </p>
              <p className="text-xs text-[#A0AABF] leading-relaxed mb-6">
                Don't see the email? Check your spam folder, or contact the PLANIVANCE AI team. During the private beta the link may be delivered via your administrator instead of email.
              </p>
              <Link
                to="/auth"
                data-testid="forgot-back-to-signin"
                className="inline-block text-sm text-[#0A1128] underline hover:text-[#C5A059]"
              >
                Return to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
